import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { adminApi } from '../api';

const emptyProduct = {
  name: '',
  description: '',
  code: '',
  category: '',
  technicalSpecs: '',
  pageUrl: '',
  urlImage: '',
  urlVideo: '',
  url3Dviewer: '',
  sortOrder: 0,
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');

  const load = () =>
    adminApi.getProducts().then(setProducts).catch((err) => setError(err.message));

  useEffect(() => {
    load();
  }, []);

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const uploadTo = async (field, file) => {
    if (!file) return;
    try {
      const data = await adminApi.upload(file);
      setForm((prev) => ({ ...prev, [field]: data.url }));
    } catch (err) {
      setError(err.message);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyProduct);
    setOpen(true);
  };

  const openEdit = (product) => {
    setEditingId(product.id);
    setForm({ ...emptyProduct, ...product, code: product.code || '', sortOrder: product.sortOrder || 0 });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) await adminApi.updateProduct(editingId, form);
      else await adminApi.createProduct(form);
      setOpen(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    try {
      await adminApi.deleteProduct(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Prodotti
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nuovo prodotto
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Codice</TableCell>
              <TableCell>Media</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.category}</TableCell>
                <TableCell>{product.code}</TableCell>
                <TableCell>
                  {[product.urlImage && 'img', product.urlVideo && 'video', product.url3Dviewer && '3D']
                    .filter(Boolean)
                    .join(', ') || '—'}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(product)}><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDelete(product.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>Nessun prodotto. Aggiungi il primo dal pulsante in alto.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Modifica prodotto' : 'Nuovo prodotto'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
          <TextField label="Nome" value={form.name} onChange={setField('name')} required fullWidth />
          <TextField label="Descrizione" value={form.description} onChange={setField('description')} fullWidth multiline />
          <TextField label="Caratteristiche tecniche" value={form.technicalSpecs} onChange={setField('technicalSpecs')} fullWidth multiline />
          <TextField label="Categoria" value={form.category} onChange={setField('category')} fullWidth />
          <TextField label="Codice" value={form.code} onChange={setField('code')} fullWidth />
          <TextField label="Link pagina" value={form.pageUrl} onChange={setField('pageUrl')} fullWidth />
          <TextField label="URL immagine" value={form.urlImage} onChange={setField('urlImage')} fullWidth />
          <Button component="label" variant="outlined">
            Carica immagine
            <input hidden type="file" accept="image/*" onChange={(e) => uploadTo('urlImage', e.target.files[0])} />
          </Button>
          <TextField label="URL video" value={form.urlVideo} onChange={setField('urlVideo')} fullWidth />
          <Button component="label" variant="outlined">
            Carica video
            <input hidden type="file" accept="video/mp4,video/webm" onChange={(e) => uploadTo('urlVideo', e.target.files[0])} />
          </Button>
          <TextField label="URL viewer 3D" value={form.url3Dviewer} onChange={setField('url3Dviewer')} fullWidth helperText="Incolla l'URL dell'iframe 3D" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
